/**
 * Marketplace Discovery — Public consumer page (mobile-first).
 *
 * Public routes:
 *    /m                     → search + grid
 *    /m/s/:slug             → listing detail
 *    /m/c/:city             → city landing page (SEO)
 *    /m/c/:city/:category   → city × category landing page
 *
 * Reads from /api/marketplace/*. All endpoints are gated by the
 * `marketplace.enabled` and `marketplace.discovery.enabled` flags
 * — when off, the page degrades to a polite empty state.
 *
 * This page is intentionally lightweight (no auth required) and does NOT
 * touch the legacy tenant UI.
 */
import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useParams, Link, useSearchParams } from "react-router-dom";
import {
  Search, MapPin, Star, ArrowRight, Filter, Sparkles, Globe,
  ChevronRight, Heart, Calendar, Tag, CheckCircle2, Reply,
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";
const isAR = () => (document?.documentElement?.lang || "ar").startsWith("ar");

const t = (en, ar) => isAR() ? ar : en;

const Pill = ({ active, onClick, children, testId }) => (
  <button data-testid={testId} onClick={onClick} className={`px-3 py-1.5 rounded-full text-xs font-medium border transition whitespace-nowrap ${
    active
      ? "bg-white text-[#7A1F3D] border-white shadow-sm"
      : "bg-white/10 text-white border-white/30 hover:bg-white/20 hover:border-white/60"
  }`}>{children}</button>
);

const ListingCard = ({ l }) => {
  const name = l.name?.ar || l.name?.en || l.slug;
  const cover = l.media?.cover;
  return (
    <Link to={`/m/s/${l.slug}`} className="block group rounded-2xl overflow-hidden bg-white border border-stone-100 hover:shadow-lg transition" data-testid={`mp-card-${l.slug}`}>
      <div className="aspect-[4/3] relative bg-gradient-to-br from-[#f4e7d3] via-[#e4cdb7] to-[#b76e79] overflow-hidden">
        {cover ? <img src={cover} alt={name} className="absolute inset-0 w-full h-full object-cover" /> : (
          <div className="absolute inset-0 flex items-center justify-center text-white/70 font-bold text-3xl">{name?.[0] || "i"}</div>
        )}
        {l.visibility?.instant_book && (
          <span className="absolute top-2 start-2 inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-white/95 text-[10px] font-semibold text-[#1a1015]">
            <Sparkles size={10} /> {t("Instant", "حجز فوري")}
          </span>
        )}
      </div>
      <div className="p-3 sm:p-4">
        <div className="flex items-start justify-between gap-2">
          <h3 className="font-bold text-stone-900 text-sm sm:text-base line-clamp-1 flex-1">{name}</h3>
          <div className="flex items-center gap-0.5 text-xs">
            <Star size={12} className="fill-amber-400 text-amber-400" />
            <span className="font-semibold text-stone-700">{(l.rating?.avg || 0).toFixed(1)}</span>
            <span className="text-stone-400">({l.rating?.count || 0})</span>
          </div>
        </div>
        <div className="flex items-center gap-1 text-[11px] text-stone-500 mt-1">
          <MapPin size={11} /> <span className="line-clamp-1">{l.branches?.[0]?.city_id || l.vertical}</span>
        </div>
        <div className="flex items-center justify-between mt-2">
          <span className="text-xs font-semibold text-[#7A1F3D]">
            {(l.price_min ?? l.price_range?.min ?? 0)} – {(l.price_max ?? l.price_range?.max ?? 0)} <span className="text-stone-400 font-normal">{l.currency || l.price_range?.currency || "QAR"}</span>
          </span>
          <ArrowRight size={14} className="text-stone-400 group-hover:text-[#7A1F3D] group-hover:translate-x-1 rtl:rotate-180 transition" />
        </div>
      </div>
    </Link>
  );
};

// ============ Search Page (/m, /m/c/:city, /m/c/:city/:category) ============
export function MarketplaceSearch() {
  const params = useParams();
  const [sp, setSp] = useSearchParams();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [cats, setCats] = useState([]);
  const [cities, setCities] = useState([]);
  const [q, setQ] = useState(sp.get("q") || "");
  const cityFromUrl = params.city || sp.get("city") || "";
  const catFromUrl = params.category || sp.get("category") || "";
  const [activeCity, setActiveCity] = useState(cityFromUrl);
  const [activeCat, setActiveCat] = useState(catFromUrl);
  const [sort, setSort] = useState(sp.get("sort") || "relevance");

  const queryUrl = useMemo(() => {
    const u = new URLSearchParams();
    if (q) u.set("q", q);
    if (activeCat) u.set("category", activeCat);
    if (activeCity) u.set("city", activeCity);
    if (sort && sort !== "relevance") u.set("sort", sort);
    return u.toString();
  }, [q, activeCat, activeCity, sort]);

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/marketplace/categories`).then(r => r.data?.items || []).catch(() => []),
      axios.get(`${API}/marketplace/cities`).then(r => r.data?.items || []).catch(() => []),
    ]).then(([c, ci]) => { setCats(c); setCities(ci); });
  }, []);

  useEffect(() => {
    setLoading(true);
    setError(null);
    axios.get(`${API}/marketplace/search?${queryUrl}`)
      .then(r => { setItems(r.data?.items || []); setTotal(r.data?.total || 0); })
      .catch(e => setError(e?.response?.data?.detail || "marketplace not enabled"))
      .finally(() => setLoading(false));
  }, [queryUrl]);

  const updateSp = (next) => {
    const u = new URLSearchParams(sp);
    Object.entries(next).forEach(([k, v]) => {
      if (v) u.set(k, v); else u.delete(k);
    });
    setSp(u, { replace: true });
  };

  const title = useMemo(() => {
    if (activeCity && activeCat) {
      const c = cities.find(c => c.city_id === activeCity);
      const k = cats.find(k => k.category_id === activeCat);
      return `${k?.name?.ar || k?.name?.en || activeCat} · ${c?.name?.ar || c?.name?.en || activeCity}`;
    }
    if (activeCity) {
      const c = cities.find(c => c.city_id === activeCity);
      return t("Salons in", "صالونات في") + " " + (c?.name?.ar || c?.name?.en || activeCity);
    }
    return t("Discover salons, spa & wellness in Qatar", "اكتشف صالونات وسبا في قطر");
  }, [activeCity, activeCat, cities, cats]);

  // SEO
  useEffect(() => {
    document.title = `${title} · iSaloon`;
    const meta = document.querySelector('meta[name="description"]') || (() => {
      const m = document.createElement('meta'); m.name = "description"; document.head.appendChild(m); return m;
    })();
    meta.content = `${title}. ${t("Book beauty, spa, and wellness services on iSaloon.", "احجز خدمات التجميل والسبا والعافية في آي صالون.")}`;
  }, [title]);

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#fdf9f3] to-[#f4ece0]" data-testid="marketplace-discover-page">
      {/* Hero */}
      <div className="bg-gradient-to-br from-[#5C1230] via-[#7A1F3D] to-[#9B2950] text-white">
        <div className="max-w-6xl mx-auto px-4 py-10 sm:py-14">
          <div className="flex items-center gap-2 text-[11px] uppercase tracking-widest text-white/80 font-medium">
            <Globe size={12} /> iSaloon Marketplace
          </div>
          <h1 className="text-2xl sm:text-4xl font-bold mt-2 leading-tight text-white">{title}</h1>
          <p className="text-white/90 text-sm mt-2 max-w-xl">{t("Find and book your next appointment in seconds.", "ابحث واحجز موعدك القادم في ثوانٍ.")}</p>

          <div className="mt-5 bg-white rounded-2xl shadow-xl ring-1 ring-black/5 p-2 flex items-center gap-2 flex-wrap sm:flex-nowrap">
            <Search className="text-stone-500 ms-2" size={18} />
            <input
              data-testid="mp-search-input"
              value={q}
              onChange={(e) => { setQ(e.target.value); updateSp({ q: e.target.value }); }}
              placeholder={t("Search salon, service, or treatment…", "ابحث عن صالون أو خدمة…")}
              className="flex-1 outline-none px-2 py-2 text-stone-900 placeholder-stone-500 text-sm min-w-0"
            />
            <select
              data-testid="mp-search-sort"
              value={sort}
              onChange={(e) => { setSort(e.target.value); updateSp({ sort: e.target.value }); }}
              className="px-3 py-2 text-sm rounded-lg bg-stone-50 border border-stone-300 text-stone-800 focus:border-[#7A1F3D] focus:outline-none"
            >
              <option value="relevance">{t("Relevance", "الأنسب")}</option>
              <option value="rating">{t("Top rated", "الأعلى تقييماً")}</option>
              <option value="price_asc">{t("Price ↑", "السعر تصاعدي")}</option>
              <option value="price_desc">{t("Price ↓", "السعر تنازلي")}</option>
              <option value="newest">{t("Newest", "الأحدث")}</option>
            </select>
          </div>

          {/* City pills */}
          <div className="mt-4 flex gap-2 overflow-x-auto pb-1 -mx-4 px-4 sm:mx-0 sm:px-0">
            <Pill testId="city-all" active={!activeCity} onClick={() => { setActiveCity(""); updateSp({ city: "" }); }}>{t("All cities", "كل المدن")}</Pill>
            {cities.map(c => (
              <Pill key={c.city_id} testId={`city-${c.city_id}`} active={activeCity === c.city_id} onClick={() => { setActiveCity(c.city_id); updateSp({ city: c.city_id }); }}>
                {c.name?.ar || c.name?.en}
              </Pill>
            ))}
          </div>
          {/* Category pills */}
          <div className="mt-3 flex gap-2 overflow-x-auto pb-1 -mx-4 px-4 sm:mx-0 sm:px-0">
            <Pill testId="cat-all" active={!activeCat} onClick={() => { setActiveCat(""); updateSp({ category: "" }); }}>{t("All categories", "كل التصنيفات")}</Pill>
            {cats.map(c => (
              <Pill key={c.category_id} testId={`cat-${c.category_id}`} active={activeCat === c.category_id} onClick={() => { setActiveCat(c.category_id); updateSp({ category: c.category_id }); }}>
                {c.name?.ar || c.name?.en}
              </Pill>
            ))}
          </div>
        </div>
      </div>

      {/* Results */}
      <div className="max-w-6xl mx-auto px-4 py-6 sm:py-8">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-sm text-stone-600">
            {loading ? t("Searching…", "جاري البحث…") : `${total} ${t("results", "نتيجة")}`}
          </h2>
        </div>

        {error && (
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-6 text-center text-amber-800 text-sm" data-testid="mp-error">
            {error === "marketplace not enabled" ?
              t("The marketplace is in private preview. Come back soon!", "السوق العام في معاينة خاصة الآن. عُد قريباً!") :
              error
            }
          </div>
        )}

        {!error && !loading && items.length === 0 && (
          <div className="text-center py-16 text-stone-500" data-testid="mp-empty">
            <Filter size={32} className="mx-auto mb-3 text-stone-300" />
            <div className="font-semibold">{t("No salons match", "لا توجد نتائج")}</div>
            <div className="text-sm mt-1">{t("Try removing filters or widening your search.", "جرّب إزالة المرشّحات أو توسيع البحث.")}</div>
          </div>
        )}

        {!error && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="mp-grid">
            {items.map((l) => <ListingCard key={l.listing_id} l={l} />)}
          </div>
        )}
      </div>

      {/* SEO breadcrumbs / linked city pages */}
      {!activeCity && cities.length > 0 && (
        <div className="bg-white border-t border-stone-200 py-8">
          <div className="max-w-6xl mx-auto px-4">
            <h3 className="text-sm font-bold text-stone-700 mb-3">{t("Browse by city", "تصفّح حسب المدينة")}</h3>
            <div className="flex flex-wrap gap-2">
              {cities.map(c => (
                <Link key={c.city_id} to={`/m/c/${c.city_id}`} className="px-3 py-1.5 rounded-full bg-stone-100 hover:bg-stone-200 text-xs text-stone-700">
                  {c.name?.ar || c.name?.en}
                </Link>
              ))}
            </div>
          </div>
        </div>
      )}

      <footer className="text-center text-xs text-stone-600 py-6">
        iSaloon · {t("Beauty, Wellness & Fitness in Qatar", "تجميل وعافية ولياقة في قطر")}
      </footer>
    </div>
  );
}

// ============ Listing Detail Page (/m/s/:slug) ============
function ReviewsSection({ listingId, ratingAvg, ratingCount }) {
  const [items, setItems] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [unavailable, setUnavailable] = React.useState(false);

  React.useEffect(() => {
    if (!listingId) return;
    setLoading(true); setUnavailable(false);
    axios.get(`${API}/marketplace/reviews/listing/${listingId}`)
      .then((r) => setItems(r.data.items || []))
      .catch((e) => {
        if (e?.response?.status === 503) setUnavailable(true);
        setItems([]);
      })
      .finally(() => setLoading(false));
  }, [listingId]);

  return (
    <section className="bg-white rounded-2xl p-4 sm:p-5 border border-stone-100" data-testid="mp-reviews">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <h2 className="font-bold text-stone-800 flex items-center gap-2">
          <Star size={16} className="text-amber-400 fill-amber-400" /> {t("Reviews", "المراجعات")}
        </h2>
        {(ratingCount || 0) > 0 && (
          <div className="text-sm text-stone-600">
            <span className="font-bold text-stone-800 text-base">{(ratingAvg || 0).toFixed(1)}</span>
            <span className="text-stone-400 ms-1">/ 5 · {ratingCount} {t("review(s)", "مراجعة")}</span>
          </div>
        )}
      </div>

      {loading && (
        <div className="mt-4 space-y-3" data-testid="mp-reviews-skeleton">
          {[1, 2, 3].map((i) => (
            <div key={i} className="animate-pulse">
              <div className="h-3 bg-stone-200 rounded w-1/4 mb-2" />
              <div className="h-2 bg-stone-100 rounded w-3/4 mb-1" />
              <div className="h-2 bg-stone-100 rounded w-2/3" />
            </div>
          ))}
        </div>
      )}

      {!loading && unavailable && (
        <div className="mt-3 text-sm text-stone-400">
          {t("Reviews are not enabled yet.", "ميزة المراجعات غير مفعّلة بعد.")}
        </div>
      )}

      {!loading && !unavailable && items.length === 0 && (
        <div className="mt-3 text-sm text-stone-400">
          {t("No public reviews yet — be the first!", "لا توجد مراجعات عامة بعد — كن الأول!")}
        </div>
      )}

      {!loading && !unavailable && items.length > 0 && (
        <div className="mt-3 divide-y divide-stone-100">
          {items.map((r) => (
            <div key={r.review_id} className="py-4" data-testid={`mp-review-${r.review_id}`}>
              <div className="flex items-center gap-2 flex-wrap">
                <div className="flex items-center gap-0.5">
                  {[1, 2, 3, 4, 5].map((n) => (
                    <Star key={n} size={13} className={n <= (r.ratings?.overall || 0) ? "fill-amber-400 text-amber-400" : "text-stone-300"} />
                  ))}
                </div>
                {r.verified && (
                  <span
                    className="inline-flex items-center gap-1 text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200"
                    title={t("Submitted after a real completed booking on iSaloon", "مرسلة بعد حجز فعلي مكتمل على iSaloon")}
                    data-testid={`mp-review-verified-${r.review_id}`}
                  >
                    <CheckCircle2 size={10} /> {t("Verified booking", "حجز موثّق")}
                  </span>
                )}
                <span className="text-[10px] text-stone-400">{(r.ts || "").slice(0, 10)}</span>
              </div>
              {(r.text?.ar || r.text?.en) && (
                <p className="text-sm text-stone-700 mt-1.5 leading-relaxed" dir="auto">
                  {isAR() ? (r.text?.ar || r.text?.en) : (r.text?.en || r.text?.ar)}
                </p>
              )}
              {/* Tenant reply (Sprint A) — nested below the review */}
              {r.reply && (r.reply.text_ar || r.reply.text_en) && (
                <div
                  className="mt-3 ms-3 sm:ms-6 ps-3 sm:ps-4 border-s-2 border-[#7A1F3D]/40 bg-amber-50/40 rounded-r-lg py-2 pe-3"
                  data-testid={`mp-review-reply-${r.review_id}`}
                >
                  <div className="flex items-center gap-1.5 text-[11px] font-semibold text-[#8b6427]">
                    <Reply size={11} />
                    <span>{t("Salon's reply", "رد الصالون")}</span>
                    <span className="text-stone-400 font-normal">· {(r.reply.replied_at || "").slice(0, 10)}</span>
                  </div>
                  <p className="text-[13px] text-stone-700 mt-1 leading-relaxed" dir="auto">
                    {isAR() ? (r.reply.text_ar || r.reply.text_en) : (r.reply.text_en || r.reply.text_ar)}
                  </p>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

export function MarketplaceListingDetail() {
  const { slug } = useParams();
  const [l, setL] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    setLoading(true);
    axios.get(`${API}/marketplace/listings/${slug}`)
      .then(r => setL(r.data))
      .catch(e => setError(e?.response?.data?.detail || "not found"))
      .finally(() => setLoading(false));
  }, [slug]);

  useEffect(() => {
    if (!l) return;
    const name = l.name?.ar || l.name?.en;
    document.title = `${name} · iSaloon`;
  }, [l]);

  if (loading) return <div className="min-h-screen flex items-center justify-center text-stone-500">…</div>;
  if (error || !l) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4">
        <div className="text-center max-w-md">
          <div className="font-bold text-stone-800 text-lg">{t("Not available", "غير متاح")}</div>
          <div className="text-sm text-stone-500 mt-2">{error}</div>
          <Link to="/m" className="inline-block mt-4 text-[#7A1F3D] text-sm">← {t("Back to marketplace", "عودة للسوق")}</Link>
        </div>
      </div>
    );
  }

  const name = l.name?.ar || l.name?.en;

  return (
    <div className="min-h-screen bg-[#fdf9f3]" data-testid="mp-listing-detail">
      {/* Cover hero */}
      <div className="relative bg-gradient-to-br from-[#5C1230] via-[#7A1F3D] to-[#9B2950] text-white">
        <div className="absolute inset-0 opacity-30">
          {l.media?.cover && <img src={l.media.cover} alt={name} className="w-full h-full object-cover" />}
        </div>
        <div className="relative max-w-5xl mx-auto px-4 py-10 sm:py-14">
          <Link to="/m" className="text-xs text-white/80 hover:text-white inline-flex items-center gap-1"><ChevronRight size={12} className="rotate-180 rtl:rotate-0" /> {t("Marketplace", "السوق")}</Link>
          <h1 className="text-2xl sm:text-4xl font-bold mt-3 text-white">{name}</h1>
          <div className="flex items-center gap-4 mt-3 text-sm flex-wrap text-white">
            <span className="inline-flex items-center gap-1"><Star size={14} className="fill-amber-300 text-amber-300" /> {(l.rating?.avg || 0).toFixed(1)} · {l.rating?.count || 0}</span>
            {l.vertical && <span className="px-2 py-0.5 rounded-full bg-white/20 text-xs">{l.vertical}</span>}
            {(l.tags || []).slice(0, 3).map(tag => <span key={tag} className="px-2 py-0.5 rounded-full bg-white/20 text-xs">#{tag}</span>)}
          </div>
          {l.about && (l.about.ar || l.about.en) && (
            <p className="mt-4 text-white/90 text-sm max-w-2xl leading-relaxed">{l.about.ar || l.about.en}</p>
          )}
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 py-6 sm:py-8 grid sm:grid-cols-3 gap-6">
        <div className="sm:col-span-2 space-y-6">
          {/* Branches */}
          <section className="bg-white rounded-2xl p-4 sm:p-5 border border-stone-100" data-testid="mp-branches">
            <h2 className="font-bold text-stone-800 flex items-center gap-2"><MapPin size={16} className="text-[#7A1F3D]" /> {t("Branches", "الفروع")}</h2>
            <div className="mt-3 space-y-2">
              {(l.branches || []).map((b, i) => (
                <div key={i} className="flex items-start justify-between gap-3 p-3 rounded-lg bg-stone-50 text-sm">
                  <div>
                    <div className="font-semibold text-stone-800">{(typeof b.name === "object" ? (b.name?.ar || b.name?.en) : b.name) || `Branch ${i + 1}`}</div>
                    <div className="text-[11px] text-stone-500">{b.phone || ""}</div>
                  </div>
                  {b.geo && (
                    <a href={`https://www.google.com/maps?q=${b.geo.coordinates[1]},${b.geo.coordinates[0]}`} target="_blank" rel="noopener noreferrer" className="text-xs text-[#7A1F3D] hover:underline">
                      {t("View on map", "الخريطة")} →
                    </a>
                  )}
                </div>
              ))}
              {(l.branches || []).length === 0 && <div className="text-sm text-stone-500">{t("No branches yet.", "لا فروع بعد.")}</div>}
            </div>
          </section>

          {/* Price */}
          {l.visibility?.show_prices !== false && (
            <section className="bg-white rounded-2xl p-4 sm:p-5 border border-stone-100" data-testid="mp-price">
              <h2 className="font-bold text-stone-800 flex items-center gap-2"><Tag size={16} className="text-[#7A1F3D]" /> {t("Price range", "نطاق الأسعار")}</h2>
              <div className="mt-3 text-2xl font-bold text-[#7A1F3D]">
                {(l.price_range?.min || 0)} – {(l.price_range?.max || 0)} <span className="text-base text-stone-400 font-normal">{l.price_range?.currency || "QAR"}</span>
              </div>
            </section>
          )}

          {/* Reviews (Phase 5) — approved only */}
          <ReviewsSection
            listingId={l.listing_id}
            ratingAvg={l.rating?.avg || 0}
            ratingCount={l.rating?.count || 0}
          />
        </div>

        {/* Sidebar: book */}
        <aside className="space-y-3">
          <div className="bg-white rounded-2xl p-4 sm:p-5 border border-stone-100 sticky top-4">
            <h3 className="font-bold text-stone-800 flex items-center gap-2"><Calendar size={16} className="text-[#7A1F3D]" /> {t("Book now", "احجز الآن")}</h3>
            <p className="text-xs text-stone-500 mt-2 leading-relaxed">{t("Booking from the marketplace will be available soon. Tap below for direct contact.", "الحجز من السوق متاح قريباً. للتواصل المباشر اضغط أدناه.")}</p>
            {l.branches?.[0]?.phone && (
              <a href={`tel:${l.branches[0].phone}`} className="mt-3 block text-center px-4 py-2.5 rounded-lg bg-gradient-to-r from-[#7A1F3D] to-[#9B2950] text-white text-sm font-semibold">
                📞 {l.branches[0].phone}
              </a>
            )}
            <button data-testid="mp-favorite-btn" className="mt-2 w-full inline-flex items-center justify-center gap-2 px-4 py-2 rounded-lg border border-stone-200 hover:border-rose-300 text-sm text-stone-700">
              <Heart size={14} /> {t("Save", "حفظ")}
            </button>
          </div>
        </aside>
      </div>
    </div>
  );
}
